import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, X, RefreshCw, ChevronDown } from 'lucide-react';
import './ErrorModal.css';

function ErrorModal({ isVisible, onClose, errorMessage, errorCode, onRetry }) {
  const { t } = useTranslation('chat');
  const navigate = useNavigate();

  if (!isVisible) return null;

  // A document that failed on our side is tried again right here, from what it left stored; any other failure
  // starts over from the dashboard - the app's new-document entry point (upload / scan).
  const handleTryAgain = () => {
    onClose();
    if (onRetry) onRetry();
    else navigate('/');
  };

  const getErrorTitle = () => {
    if (errorCode === 'MONTHLY_LIMIT_REACHED') return t('errorModal.titleMonthlyLimit');
    if (errorCode === 'LLM_FORMAT_ERROR') return t('errorModal.titleFormat');
    if (errorCode === 'WORKER_DIED') return t('errorModal.titleWorkerDied');
    if (
      errorCode === 'RASTERIZE_TIMEOUT' ||
      errorCode === 'OCR_TIMEOUT' ||
      errorCode === 'PREPROCESS_TIMEOUT'
    ) return t('errorModal.titleTimeout');
    return t('errorModal.title');
  };

  const getErrorDescription = () => {
    if (errorCode === 'MONTHLY_LIMIT_REACHED') return t('errorModal.descriptionMonthlyLimit');
    if (errorCode === 'LLM_FORMAT_ERROR') return t('errorModal.descriptionFormat');
    if (errorCode === 'WORKER_DIED') return t('errorModal.descriptionWorkerDied');
    if (
      errorCode === 'RASTERIZE_TIMEOUT' ||
      errorCode === 'OCR_TIMEOUT' ||
      errorCode === 'PREPROCESS_TIMEOUT'
    ) return t('errorModal.descriptionTimeout');
    if (onRetry) return t('errorModal.descriptionRetry');
    return t('errorModal.description');
  };

  return (
    <div className="error-modal-overlay">
      <div className="error-modal">
        <button className="error-modal-close" onClick={onClose}>
          <X size={20} />
        </button>

        <div className="error-modal-icon">
          <AlertCircle size={48} />
        </div>

        <h2 className="error-modal-title">{getErrorTitle()}</h2>
        <p className="error-modal-message">{getErrorDescription()}</p>

        {errorMessage && (
          <details className="error-modal-details">
            <summary>
              <span>{t('errorModal.technicalDetails')}</span>
              <ChevronDown size={16} className="error-modal-chevron" />
            </summary>
            <pre className="error-modal-code">{errorMessage}</pre>
          </details>
        )}

        <button className="error-modal-button" onClick={handleTryAgain}>
          <RefreshCw size={20} />
          {onRetry ? t('errorModal.retry') : t('errorModal.tryAgain')}
        </button>

        <button className="error-modal-dismiss" onClick={onClose}>
          {t('errorModal.dismiss')}
        </button>
      </div>
    </div>
  );
}

export default ErrorModal;
